import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { validate } from '../../../validation/engine';
import ResponseShell from './ResponseShell';
import SamplePickerGate from './SamplePickerGate';
import FormControl from '../../common/FormControl';
import Skeleton from '../../common/Skeleton';
import NoDataFound from '../../common/NoDataFound';
import { useAutosave } from './useAutosave';
import { computeResponseReadOnly } from './responseShellTypes';
import type { AutosaveStatus, ProcedureResponseProps, ResponseRole } from './responseShellTypes';
import {
  responseService,
  transitionService,
  findingService,
  checklistTemplateService,
  procedureEvidenceService,
  type SampleSet,
  type SampleItem,
  type ChecklistTemplateItem,
  type Finding,
  type ProcedureEvidence,
} from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, {
  ITEM_TYPE_CHECKLIST_RESPONSE,
  ITEM_TYPE_SAMPLE_ITEM,
} from './RaiseFindingButton';
import InlineQuestionMeta from './InlineQuestionMeta';
import EvidenceCell from './EvidenceCell';
import RejectNoteCell from './RejectNoteCell';
import Modal from '../../common/Modal';
import Button from '../../common/Button';

/**
 * ChecklistResponse — body for procedure_type='Checklist'.
 *
 * Revised 2026-05-18 (migration 080): per-(sample × question) granularity.
 *
 *   - SamplePickerGate still owns sample lifecycle (active ADT_sample_sets
 *     row + items). On top of that, the body now fetches the procedure
 *     template's checklist_templates and renders one section per sample
 *     containing one row per question (Yes/No/NA + observation + evidence +
 *     inline findings).
 *   - One ADT_checklist_responses row per (sample_item_id,
 *     checklist_template_id) pair. The check_question column is denormalized
 *     from the template at write time so the row survives template edits.
 *   - Autosave per useAutosave(1000ms) after any field change; saves via
 *     responseService('Checklist', ...).create/update keyed on existing
 *     response_id per (sample, question) cell.
 *
 * Phase 113 (REQ-7/8/9):
 *   - Action cluster moved INLINE inside the question cell (no Actions column).
 *   - Icons are pure grey triggers with NO count badge.
 *   - row.observation renders inline in light grey (.response-ra-inline-remark).
 *   - Per-question evidence + findings rendered item-by-item in an inline <tr>.
 *   - Communications are instance-level (ResponseShell) — NOT per question here.
 */

type Answer = 'Yes' | 'No' | 'NA' | null;

interface ChecklistRow {
  sample_item_id: number;
  sample_label: string;
  checklist_template_id: number;
  check_question: string;
  na_allowed: boolean;
  evidence_type: string | null;
  response_id?: number;
  answer: Answer;
  observation: string;
  evidence_file_id: number | null;
  /** G2 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  dirty: boolean;
}

/**
 * RemarksModal — per-row observation editor.
 *
 * Edits a LOCAL draft and commits to the row exactly once, on Done. The earlier
 * inline version called updateRow on every keystroke, which marked the row dirty
 * and woke the 1000ms autosave on each character (status churn → re-render →
 * flicker). Local draft + single commit removes that. Validation runs live on the
 * draft (advisory only; Done still commits, matching prior non-blocking behaviour).
 */
const RemarksModal: React.FC<{
  question: string;
  initialValue: string;
  readOnly: boolean;
  testId: string;
  onValidate: (value: string) => { valid: boolean; message?: string };
  onCommit: (value: string) => void;
  onClose: () => void;
}> = ({ question, initialValue, readOnly, testId, onValidate, onCommit, onClose }) => {
  const [draft, setDraft] = useState(initialValue);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setDraft(v);
    if (!v.trim()) { setLocalErr(null); return; }
    const r = onValidate(v);
    setLocalErr(r.valid ? null : (r.message ?? 'Invalid observation.'));
  };

  const handleDone = () => {
    onCommit(draft);
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>{`Remarks under "${question}"`}</Modal.Header>
      <Modal.Body>
        <FormControl.Textarea
          label="Observation / remarks"
          data-testid={testId}
          value={draft}
          onChange={handleChange}
          rows={5}
          autoFocus
          disabled={readOnly}
        />
        {localErr && <div className="response-inline-warning">{localErr}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="fill" onClick={handleDone} disabled={readOnly}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};

interface BodyHandleRef {
  status: AutosaveStatus;
  flush: () => Promise<void>;
}

interface BodyProps {
  instance: ProcedureResponseProps['instance'];
  sampleSet: SampleSet;
  sampleItems: SampleItem[];
  handleRef: React.MutableRefObject<BodyHandleRef>;
  onStatusChange: (s: AutosaveStatus) => void;
  /** G3 — true when the instance is Approved; disables all editing affordances. */
  readOnly?: boolean;
  /** Caller's role — checker ('approver') gets the editable reject control. */
  role: ResponseRole;
  /** G2 — report the number of lines carrying a reject_note up to the shell. */
  onRejectedCount: (n: number) => void;
  /** Submit gate — report the unanswered-lines reason (null = all answered). */
  onIncompleteReason: (reason: string | null) => void;
}

const ChecklistBody: React.FC<BodyProps> = ({
  instance,
  sampleItems,
  handleRef,
  onStatusChange,
  readOnly = false,
  role,
  onRejectedCount,
  onIncompleteReason,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;

  const [rows, setRows] = useState<ChecklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  // Plan 05b-05 / STAFF-06 — inline finding raise state.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    itemTypeId: number;
    itemRecordId: number | null;
  } | null>(null);
  // Phase 113 REQ-8 — per-question evidence, fetched once and grouped
  // by `${item_type_id}-${item_record_id}` key.
  const [evidenceByQuestion, setEvidenceByQuestion] = useState<
    Map<string, ProcedureEvidence[]>
  >(new Map());

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  // Fetch ALL evidence for this procedure instance once, then group client-side.
  // Non-fatal: on error the inline list simply renders empty.
  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const map = new Map<string, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id != null && ev.item_record_id != null) {
          const key = `${ev.item_type_id}-${ev.item_record_id}`;
          const bucket = map.get(key) ?? [];
          bucket.push(ev);
          map.set(key, bucket);
        }
      }
      setEvidenceByQuestion(map);
    } catch {
      // Non-fatal — inline list renders empty.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  // Per row: prefer the response_id (item_type=1); fall back to sample_item_id
  // (item_type=7) so findings raised before autosave still attach.
  const findingsForRow = useCallback(
    (responseId: number | undefined, sampleItemId: number): Finding[] => {
      return findings.filter((f) => {
        if (responseId && f.item_type_id === ITEM_TYPE_CHECKLIST_RESPONSE && f.item_record_id === responseId) {
          return true;
        }
        if (f.item_type_id === ITEM_TYPE_SAMPLE_ITEM && f.item_record_id === sampleItemId) {
          return true;
        }
        return false;
      });
    },
    [findings],
  );

  // Evidence for a specific row, keyed by (item_type_id, item_record_id).
  const evidenceForRow = useCallback(
    (responseId: number | undefined, sampleItemId: number): ProcedureEvidence[] => {
      const itemTypeId = responseId ? ITEM_TYPE_CHECKLIST_RESPONSE : ITEM_TYPE_SAMPLE_ITEM;
      const itemRecordId = responseId ?? sampleItemId;
      return evidenceByQuestion.get(`${itemTypeId}-${itemRecordId}`) ?? [];
    },
    [evidenceByQuestion],
  );

  // Stable signature of the sample-item ID set. The load effect keys on THIS,
  // not the sampleItems array identity — the child path builds `virtualItems`
  // inline on every render, so depending on the array reference reloaded the
  // whole grid on every parent re-render (wiping in-progress answers / eating
  // radio clicks). The ID signature only changes when the actual set changes.
  const sampleItemsKey = sampleItems.map((it) => it.id).join('|');

  // Existing-responses load: 2026-05-18 — fetch templates + responses and
  // build the (sample × question) grid. Each cell keyed on
  // (sample_item_id, checklist_template_id). Re-runs when the active sample
  // set or the procedure_template_id changes.
  useEffect(() => {
    let cancelled = false;
    const procedureTemplateId = (instance as any).procedure_template_id as number | null | undefined;
    setLoading(true);
    (async () => {
      try {
        const [templates, responses] = await Promise.all([
          procedureTemplateId
            ? checklistTemplateService.listForProcedure(procedureTemplateId)
            : Promise.resolve([] as ChecklistTemplateItem[]),
          responseService.list('Checklist', instance.id),
        ]);
        // Index responses by composite (sample_item_id, checklist_template_id).
        const cellKey = (sid: number | null | undefined, tid: number | null | undefined) =>
          `${sid ?? 0}::${tid ?? 0}`;
        const byCell = new Map<string, any>(
          responses
            .filter((r: any) => r.checklist_template_id != null)
            .map((r: any) => [cellKey(r.sample_item_id, r.checklist_template_id), r]),
        );
        const merged: ChecklistRow[] = [];
        for (const it of sampleItems) {
          for (const tpl of templates) {
            const existing = byCell.get(cellKey(it.id, tpl.id));
            merged.push({
              sample_item_id: it.id,
              sample_label: (it as any).sample_item_name ?? (it as any).item_label ?? `Sample ${it.id}`,
              checklist_template_id: tpl.id,
              check_question: tpl.check_question,
              na_allowed: tpl.na_allowed !== false,
              evidence_type: tpl.evidence_type ?? null,
              response_id: existing?.id,
              answer: (existing?.answer ?? null) as Answer,
              observation: existing?.observation ?? '',
              evidence_file_id: existing?.evidence_file_id ?? null,
              reject_note: existing?.reject_note ?? '',
              dirty: false,
            });
          }
        }
        if (!cancelled) setRows(merged);
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(
            e?.response?.data?.detail ?? e?.message ?? 'Failed to load checklist responses.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // UAT FIX H / 2026-06-22 — key the load on PRIMITIVE values only
    // (instance.id + the sample-item ID signature), never the instance object or
    // the sampleItems array reference, so a parent re-render that recreates
    // either cannot retrigger the fetch and reload the questions mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance.id, sampleItemsKey]);

  const save = useCallback(async () => {
    const dirty = rows.filter(r => r.dirty && !obsErrors[`${r.sample_item_id}::${r.checklist_template_id}`]);
    const updates: Array<{ key: string; response_id?: number }> = [];
    for (const r of dirty) {
      const payload = {
        entity_id: entityId,
        procedure_instance_id: instance.id,
        sample_item_id: r.sample_item_id,
        checklist_template_id: r.checklist_template_id,
        check_question: r.check_question,
        answer: r.answer,
        observation: r.observation,
        evidence_file_id: r.evidence_file_id,
        created_by: currentUserId,
      } as any;
      const cellKey = `${r.sample_item_id}::${r.checklist_template_id}`;
      if (r.response_id) {
        await responseService.update('Checklist', r.response_id, payload);
        updates.push({ key: cellKey, response_id: r.response_id });
      } else {
        const created = await responseService.create('Checklist', payload);
        updates.push({ key: cellKey, response_id: created?.id });
      }
    }
    setRows(prev =>
      prev.map(r => {
        const u = updates.find(x => x.key === `${r.sample_item_id}::${r.checklist_template_id}`);
        if (!u) return r;
        return { ...r, response_id: u.response_id ?? r.response_id, dirty: false };
      }),
    );
  }, [rows, obsErrors, entityId, currentUserId, instance.id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  // Always keep the parent-readable handle (status + flush) current. This is a
  // ref write, not state — it never triggers a render, so doing it every render
  // is safe and keeps flush fresh for Submit/Save-Draft.
  handleRef.current = { status, flush };

  // UAT FIX H (flicker) — notify the parent ONLY when the autosave status value
  // actually changes. Previously this effect listed `flush` in its deps; `flush`
  // gets a new identity on every keystroke (it closes over `save`, which depends
  // on `rows`), so the effect re-ran per keystroke and called onStatusChange with
  // an UNCHANGED status, cascading a parent setState → re-render → indicator
  // flicker. Gating on the status value removes the redundant updates.
  const lastStatusRef = useRef(status);
  useEffect(() => {
    if (lastStatusRef.current !== status) {
      lastStatusRef.current = status;
      onStatusChange(status);
    }
  }, [status, onStatusChange]);

  // G2 — report rejected-line count up to the shell whenever it changes.
  useEffect(() => {
    onRejectedCount(rows.filter(r => r.reject_note && r.reject_note.trim()).length);
  }, [rows, onRejectedCount]);

  // Submit gate (2026-06-29) — every checklist line (sample × item) must have an
  // answer picked before the maker can submit. answer == null => unanswered.
  useEffect(() => {
    const unanswered = rows.filter(r => r.answer == null).length;
    onIncompleteReason(
      rows.length > 0 && unanswered > 0
        ? `Answer all ${rows.length} items before submitting — ${unanswered} still unanswered.`
        : null,
    );
  }, [rows, onIncompleteReason]);

  const updateRow = (idx: number, patch: Partial<ChecklistRow>) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, dirty: true };
      return next;
    });
    markDirty();
  };

  const handleObsBlur = (rowKey: string, value: string) => {
    if (!value || !value.trim()) {
      setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    const r = validate(110, value);
    if (!r.valid) setObsErrors(prev => ({ ...prev, [rowKey]: r.message ?? 'Invalid observation.' }));
    else setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
  };

  // Remarks (D-3): the per-row observation/remark is captured in a small modal
  // opened by the Remarks icon. `remarksRow` holds the row index.
  const [remarksRow, setRemarksRow] = useState<number | null>(null);

  // Group rows by sample for per-sample section rendering. Within each
  // section, rows preserve question order from checklist_templates.
  const sampleSections: Array<{ sample_item_id: number; sample_label: string; rows: Array<{ row: ChecklistRow; idx: number }> }> = [];
  rows.forEach((r, idx) => {
    let section = sampleSections.find(s => s.sample_item_id === r.sample_item_id);
    if (!section) {
      section = { sample_item_id: r.sample_item_id, sample_label: r.sample_label, rows: [] };
      sampleSections.push(section);
    }
    section.rows.push({ row: r, idx });
  });

  const hasQuestions = rows.length > 0;

  // Phase 113: 3 columns (S.No / Question / Answer) — no Actions column.
  const COL_COUNT = 3;

  return (
    <>
      {loadError && (
        <div className="response-load-error">{loadError}</div>
      )}

      {loading && (
        <div className="response-section">
          <div className="table-list response-qtable">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <colgroup>
                  <col className="response-col-sno" />
                  <col />
                  <col className="response-col-answer" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="table-list-th response-th-sno">S.No</th>
                    <th className="table-list-th">Question</th>
                    <th className="table-list-th">Answer</th>
                  </tr>
                </thead>
                <tbody aria-busy={true} aria-live="polite">
                  <Skeleton.TableRows
                    cols={['2xs', 'lg', 'md']}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && !hasQuestions && (
        <NoDataFound
          title="No checklist questions"
          message="No checklist questions have been configured for this procedure template yet. Ask your GRC admin to add questions under the procedure template before running this audit."
        />
      )}

      {!loading && sampleSections.map(section => (
        <div key={section.sample_item_id} className="response-section">
          <div className="table-list response-qtable">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <colgroup>
                  <col className="response-col-sno" />
                  <col />
                  <col className="response-col-answer" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="table-list-th response-th-sno">S.No</th>
                    <th className="table-list-th">Question</th>
                    <th className="table-list-th">Answer</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {section.rows.map(({ row: r, idx }, sIdx) => {
                    const rowFindings = findingsForRow(r.response_id, r.sample_item_id);
                    const rowEvidence = evidenceForRow(r.response_id, r.sample_item_id);
                    const responseValueSummary =
                      (r.answer ? `Answer: ${r.answer}` : 'No answer yet') +
                      (r.observation ? ` · ${r.observation.slice(0, 60)}` : '');
                    const radioName = `ans-${r.sample_item_id}-${r.checklist_template_id}`;
                    const answerOptions: Answer[] = r.na_allowed
                      ? ['Yes', 'No', 'NA']
                      : ['Yes', 'No'];

                    return (
                      <React.Fragment key={`${r.sample_item_id}-${r.checklist_template_id}`}>
                        <tr className="table-list-row">
                          <td className="table-list-cell response-cell-sno">{sIdx + 1}</td>
                          <td className="table-list-cell response-cell-question">
                            {/* REQ-7: question text LEFT, action cluster RIGHT, same flex row. */}
                            <div className="response-question-row">
                              <span className="response-question-text">{r.check_question}</span>
                              <div className="response-action-cluster response-action-cluster--inline">
                                <button
                                  type="button"
                                  className="response-row-action"
                                  aria-label="Add remark"
                                  title={r.observation ? 'Edit remark' : 'Add remark'}
                                  data-testid={`BTN_checklist_response_${r.sample_item_id}_${r.checklist_template_id}_remarks`}
                                  onClick={() => setRemarksRow(idx)}
                                  disabled={readOnly}
                                >
                                  <MessageSquare size={14} />
                                </button>
                                <EvidenceCell
                                  scope="row"
                                  evidenceType={r.evidence_type}
                                  entityId={entityId}
                                  createdBy={currentUserId}
                                  disabled={readOnly}
                                  procedureInstanceId={instance.id}
                                  itemTypeId={r.response_id ? ITEM_TYPE_CHECKLIST_RESPONSE : ITEM_TYPE_SAMPLE_ITEM}
                                  itemRecordId={r.response_id ?? r.sample_item_id}
                                  questionLabel={r.check_question}
                                  showCount={false}
                                  onChanged={reloadEvidence}
                                />
                                <RejectNoteCell
                                  responseId={r.response_id}
                                  rejectNote={r.reject_note}
                                  procedureType="Checklist"
                                  role={role}
                                  readOnly={readOnly}
                                  onSaved={(note) =>
                                    setRows(prev => prev.map((x, i) => (i === idx ? { ...x, reject_note: note } : x)))
                                  }
                                />
                                <RaiseFindingButton
                                  count={rowFindings.length}
                                  showCount={false}
                                  disabled={readOnly}
                                  aria-label="Raise finding"
                                  title="Raise a finding from this row"
                                  onClick={() => {
                                    setModalRow({
                                      context: {
                                        procedureName: instance.audit_procedure_name,
                                        sampleLabel: `${r.sample_label} · ${r.check_question.slice(0, 60)}`,
                                        responseValue: responseValueSummary,
                                      },
                                      itemTypeId: r.response_id ? ITEM_TYPE_CHECKLIST_RESPONSE : ITEM_TYPE_SAMPLE_ITEM,
                                      itemRecordId: r.response_id ?? r.sample_item_id,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                            {/* REQ-8 rev2: stacked Remarks / Attachments / Findings areas. */}
                            <InlineQuestionMeta
                              remark={r.observation}
                              evidence={rowEvidence}
                              findings={rowFindings}
                              readOnly={readOnly}
                              onEvidenceRemoved={reloadEvidence}
                              onRemarkClick={readOnly ? undefined : () => setRemarksRow(idx)}
                            />
                          </td>
                          <td className="table-list-cell">
                            <div className="response-radio-group">
                              {answerOptions.map(opt => (
                                <FormControl.Radio
                                  key={String(opt)}
                                  name={radioName}
                                  value={String(opt)}
                                  label={String(opt)}
                                  checked={r.answer === opt}
                                  onChange={() => updateRow(idx, { answer: opt })}
                                  disabled={readOnly}
                                />
                              ))}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      {/* Remarks modal — captures the per-row observation (replaces the inline
          Observation column). The textarea edits a LOCAL draft only; the row is
          committed (and autosave fires) once, on Done. Editing keystroke-by-
          keystroke previously called updateRow on every change, which triggered
          the debounced autosave + parent refetch and made the page flicker. */}
      {remarksRow !== null && rows[remarksRow] && (() => {
        const ridx = remarksRow;
        const row = rows[ridx];
        const rowKey = `${row.sample_item_id}::${row.checklist_template_id}`;
        return (
          <RemarksModal
            question={row.check_question}
            initialValue={row.observation}
            readOnly={readOnly}
            testId={`INP_checklist_remarks_${row.sample_item_id}_${row.checklist_template_id}`}
            onValidate={(v) => validate(110, v)}
            onClose={() => setRemarksRow(null)}
            onCommit={(value) => {
              handleObsBlur(rowKey, value);
              updateRow(ridx, { observation: value });
            }}
          />
        );
      })()}

      {/* Plan 05b-05 / STAFF-06 — single ObservationModal + FindingDetailModal */}
      <ObservationModal
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: instance.audit_procedure_name }}
        itemTypeId={modalRow?.itemTypeId ?? ITEM_TYPE_CHECKLIST_RESPONSE}
        itemRecordId={modalRow?.itemRecordId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
    </>
  );
};

const ChecklistResponse: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const handleRef = useRef<BodyHandleRef>({
    status: 'idle',
    flush: async () => {},
  });
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [rejectedCount, setRejectedCount] = useState(0);
  // Submit gate — reason the maker can't submit yet (unanswered lines), bubbled
  // up from ChecklistBody. Null = all lines answered.
  const [incompleteReason, setIncompleteReason] = useState<string | null>(null);
  // Read-only unless the maker (performer) is editing an open instance — shared
  // rule across all procedure types. See computeResponseReadOnly.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  // Child instances (spawned per-sample) carry sample_item_id on the instance
  // itself. They bypass SamplePickerGate — the gate is for parent procedures
  // that still need sample selection. A child renders a flat checklist scoped
  // to its one sample item only.
  const sampleItemId = (instance as any).sample_item_id as number | null | undefined;
  const sampleItemName = (instance as any).sample_item_name as string | null | undefined;
  const isChild = sampleItemId != null;

  const shellProps = {
    instance,
    procedureType: 'Checklist' as const,
    role,
    performerName,
    approverName,
    autosaveStatus,
    rejectedCount,
    incompleteReason,
    onSaveDraft: async () => { await handleRef.current.flush(); },
    onSubmit: async () => {
      await handleRef.current.flush();
      await transitionService.submit('procedure', instance.id);
      onSaved?.();
    },
    onApprove: async () => {
      await transitionService.approve('procedure', instance.id);
      onSaved?.();
    },
    onReject: async (comment: string) => {
      await transitionService.reject('procedure', instance.id, comment);
      onSaved?.();
    },
    onReopen: async () => {
      await transitionService.reopen('procedure', instance.id);
      onSaved?.();
    },
  };

  if (isChild) {
    const virtualItems: SampleItem[] = [{
      id: sampleItemId!,
      sample_set_id: 0,
      item_label: sampleItemName ?? instance.audit_procedure_name,
    }];
    return (
      <ResponseShell {...shellProps}>
        <ChecklistBody
          key={instance.id}
          instance={instance}
          sampleSet={{} as SampleSet}
          sampleItems={virtualItems}
          handleRef={handleRef}
          onStatusChange={setAutosaveStatus}
          readOnly={readOnly}
          role={role}
          onRejectedCount={setRejectedCount}
          onIncompleteReason={setIncompleteReason}
        />
      </ResponseShell>
    );
  }

  return (
    <ResponseShell {...shellProps}>
      <SamplePickerGate instance={instance} procedureType="Checklist">
        {(sampleSet, sampleItems) => (
          <ChecklistBody
            key={instance.id}
            instance={instance}
            sampleSet={sampleSet}
            sampleItems={sampleItems}
            handleRef={handleRef}
            onStatusChange={setAutosaveStatus}
            readOnly={readOnly}
            role={role}
            onRejectedCount={setRejectedCount}
            onIncompleteReason={setIncompleteReason}
          />
        )}
      </SamplePickerGate>
    </ResponseShell>
  );
};

export default ChecklistResponse;
